import React from 'react';
import { Box, Typography } from '@mui/material';

export interface AmountBar {
  label: string;
  amount: number;
  /** Shown instead of the formatted amount, e.g. "12 calls". */
  detail?: string;
}

interface Props {
  rows: AmountBar[];
  format: (amount: number) => string;
  /** Read out before the list, e.g. "Cost by service". */
  label: string;
  empty?: string;
}

/** A plain horizontal bar per row, scaled to the largest; the numbers are in the text, the bars are an aid. */
const AmountBars: React.FC<Props> = ({ rows, format, label, empty = 'Nothing to show.' }) => {
  const max = Math.max(0, ...rows.map((row) => row.amount));
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Box component="ul" aria-label={label} sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 0.75 }}>
      {rows.map((row) => (
        <Box
          component="li"
          key={row.label}
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 220px) minmax(0, 1fr) auto' }, columnGap: 1.5, alignItems: 'center' }}
        >
          <Typography variant="body2" noWrap title={row.label}>
            {row.label}
          </Typography>
          <Box aria-hidden sx={{ display: { xs: 'none', sm: 'block' }, height: 8, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Box sx={{ height: '100%', borderRadius: 1, bgcolor: 'primary.main', width: `${max > 0 ? Math.max(1, (row.amount / max) * 100) : 0}%` }} />
          </Box>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
            {row.detail ?? format(row.amount)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default AmountBars;
